// Schemas used in API endpoints
import { z } from 'zod';
import { IntegrationStatus, IntegrationType, OperationStatus } from './models/index.js';
import {
  ActivityDataEntityType,
  DateFilter,
  DateTimeSchema,
  DurationLikeObject,
  ObjectIdSchema,
  ObjectIdString,
  OperationExportFormats,
} from './common.js';
import { LlmInputMetadata } from '../interfaces.js';

export { ActivityDataEntityType };

export const OperationsSearchQuery = z.object({
  dateFilter: DateFilter.nullish(),
  status: OperationStatus.array().nullish(),
  candidateId: z.union([ObjectIdString, z.string()]).nullish(),
  accountId: ObjectIdSchema.nullish(),
  page: z.number().min(1).default(1),
  count: z.number().min(1).max(100).default(50),
});
export type OperationsSearchQuery = z.infer<typeof OperationsSearchQuery>;

export const OperationsExportInput = z.object({
  dateFilter: DateFilter.nullish(),
  status: OperationStatus.array().nullish(),
  candidateId: z.union([ObjectIdString, z.string()]).nullish(),
  parentOperationId: ObjectIdSchema.nullish(),
  accountId: ObjectIdSchema,
  format: OperationExportFormats,
});
export type OperationsExportInput = z.infer<typeof OperationsExportInput>;

export const DataIqReplayStepOptions = z
  .object({
    skip: z.number().min(0).default(0),
    limit: z.number().min(1).default(100),
    parallel: z.number().min(1).default(10),
  })
  .partial();
export type DataIqReplayStepOptions = z.infer<typeof DataIqReplayStepOptions>;

//--------------------------------------------------------------------------------------------------------------------//
//region ActivityData/TalentIQ API schemas

export { InputEntity, TrackedEntity, TrackedEntityType } from './common.js';

export const DepartmentFilterProduct = z.enum(['staffIq', 'salesIq', 'talentIq']);
export type DepartmentFilterProduct = z.infer<typeof DepartmentFilterProduct>;

export const GetActivityDataOptions = z
  .object({
    lookBack: DurationLikeObject,
    startDate: DateTimeSchema,
    endDate: DateTimeSchema,
    cacheOnly: z.boolean().optional(),
    skipActivityData: z.boolean().optional(),
    excludeConsecutiveOutbound: z.boolean().optional(),
    departmentKey: z.string().array().nullish(),
    product: DepartmentFilterProduct,
  })
  .partial();
export type GetActivityDataOptions = z.infer<typeof GetActivityDataOptions>;

export const GenerateInsightsOptions = z
  .object({
    requireAnyActivity: z.boolean(),
    requireInboundActivity: z.boolean(),
    customInput: z.looseObject({}),
    customMetadata: LlmInputMetadata.partial().optional(),
    extendSchema: z.instanceof(z.ZodObject),
    forceRefresh: z.boolean(),
    isPreload: z.boolean(),
    activityDataOptions: GetActivityDataOptions,
    departmentKey: z.string().array().nullish(),
    llmModelOverride: z.string().nullish(),
  })
  .partial();
export type GenerateInsightsOptions = z.infer<typeof GenerateInsightsOptions>;

export const GetIntegrationListOptions = z.object({
  includeMetadata: z.boolean().optional(),
});
export type GetIntegrationListOptions = z.infer<typeof GetIntegrationListOptions>;

export const GetIntegrationListParams = z
  .object({
    status: IntegrationStatus,
    type: IntegrationType,
    options: GetIntegrationListOptions,
  })
  .partial();
export type GetIntegrationListParams = z.infer<typeof GetIntegrationListParams>;

export const RefreshActivityDataOptions = z.object({
  ...GetActivityDataOptions.shape,
  forceRefresh: z.boolean().default(false),
});
export type RefreshActivityDataOptions = z.infer<typeof RefreshActivityDataOptions>;
